import { getEmojiResource } from '../emoji/utils';

/** Slack reaction names that differ from standard catalog shortNames. */
const SLACK_SHORT_NAME_ALIASES: Record<string, string[]> = {
	'+1': [':thumbsup:'],
};

export const toBareEmojiName = (emojiName: string): string =>
	emojiName.trim().replace(/^:+|:+$/g, '');

export const toEmojiShortName = (emojiName: string): string => {
	const trimmed = emojiName.trim();
	if (trimmed.startsWith(':') && trimmed.endsWith(':')) {
		return trimmed;
	}
	return `:${toBareEmojiName(trimmed)}:`;
};

export const getShortNamesToTry = (emojiName: string): string[] => {
	const bare = toBareEmojiName(emojiName);
	const shortNames = [toEmojiShortName(emojiName)];
	for (const alias of SLACK_SHORT_NAME_ALIASES[bare] ?? []) {
		if (!shortNames.includes(alias)) {
			shortNames.push(alias);
		}
	}
	return shortNames;
};

/**
 * Resolves a Slack top-reaction emoji name to a standard Unicode character via the
 * cached emoji catalog. Returns undefined when not found or on any runtime error.
 *
 * `cloudId` / `baseUrl` must match `common/ui/emoji/main.tsx` (`getEmojiResource(cloudId, baseUrl)`)
 * so we share the same in-memory `EmojiResource` and gateway URLs as other search-page emoji UI.
 */
export const resolveStandardSlackEmojiUnicode = async (
	emojiName: string | null | undefined,
	cloudId?: string,
	baseUrl?: string,
): Promise<string | undefined> => {
	if (!emojiName) {
		return undefined;
	}

	try {
		const resource = await getEmojiResource(cloudId, baseUrl);
		// EmojiResource does not fetch provider JSON on construction; findByShortName queues retries
		// until loads finish. Prime the catalog the same way ResourcedEmoji does after resolving the provider.
		await resource.getEmojiProvider();
		for (const shortName of getShortNamesToTry(emojiName)) {
			const result = await Promise.resolve(resource.findByShortName(shortName));
			if (result?.type === 'STANDARD' && result.fallback) {
				return result.fallback;
			}
		}
	} catch {
		return undefined;
	}

	return undefined;
};
